import { Link } from "react-router-dom";
import { Seo, breadcrumbSchema } from "@/lib/seo";
import { motion } from "framer-motion";
import { SOLUTIONS } from "@/data/solutions";
import { SectionHeading, Reveal } from "@/components/common";
import { ArrowRight, Compass } from "lucide-react";

export default function Solutions() {
  return (
    <div>
      <Seo
        title="Website Solutions by Industry | ATF WebWorks"
        description="Industry-specific website solutions — designs, pricing, and custom services in one place. Find the right path for your business."
        path="/solutions"
        jsonLd={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Solutions", path: "/solutions" }])]}
      />
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-5">
              <Compass size={14} /> Solutions by Industry
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.05] max-w-4xl">
              Find the perfect website for <span className="text-gold">your industry</span>
            </h1>
            <p className="text-lg text-gray-300 font-light mt-6 max-w-2xl leading-relaxed">
              Know your field but not sure which design? Start here. Each solution brings together the right designs, pricing, and custom services — all in one place.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="solutions-grid">
          {SOLUTIONS.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 0.08}>
              <Link
                to={`/solutions/${s.slug}`}
                data-testid={`solution-${s.slug}`}
                className="group relative flex flex-col h-full overflow-hidden bg-navy-800 border border-white/10 hover:border-gold/50 transition-all duration-300 hover:-translate-y-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              >
                <div className="relative h-40 overflow-hidden">
                  <img src={s.image} alt={`${s.title} website design`} loading="lazy" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-800 via-navy-800/40 to-transparent" />
                  <div className="absolute bottom-4 left-5 w-11 h-11 border border-gold/50 bg-navy/70 backdrop-blur flex items-center justify-center text-gold">
                    <s.icon size={20} strokeWidth={1.6} aria-hidden="true" />
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-heading text-xl text-white group-hover:text-gold transition-colors">{s.title}</h3>
                  <p className="text-gray-400 text-sm mt-2 leading-relaxed flex-1">{s.tagline}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-gold text-sm font-medium group-hover:gap-3 transition-all">
                    Explore solution <ArrowRight size={15} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {/* Consulting CTA */}
        <Reveal className="mt-16">
          <div className="relative overflow-hidden bg-gradient-to-br from-navy-700 to-navy-800 border border-gold/20 p-10 md:p-14 text-center grain">
            <SectionHeading center label="Not sure where you fit?" title="Let's build the right solution together" description="Tell us about your organization and we'll recommend the ideal path — a design, custom build, or a hybrid. Consulting is part of what we do." />
            <Link to="/start" data-testid="solutions-consult-cta" className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-4 font-semibold hover:bg-gold-light transition-colors mt-8">
              Start a Free Consultation <ArrowRight size={18} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
